import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Loader2, Lock, Save } from "lucide-react";
import { computeEomJournalEntry, type EomJournalEntryLine } from "@integrator-src/accounting/eom-reconciliation";

import { Button } from "@/components/ui/Button";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { Input, Label } from "@/components/ui/Input";
import { DocumentModal } from "@/pages/documents/DocumentModal";
import { cn } from "@/lib/utils";
import {
  EOM_QBO_LABELS,
  EOM_TARGET_LABELS,
  fetchEomMonth,
  formatMonthLabel,
  saveEomMonth,
  submitEomMonth,
  type EomMonthPayload,
  type EomQboValues,
  type EomTargetValues,
} from "./eomReconciliationApi";

const EOM_MONTH_QUERY_KEY = ["accounting", "eom-reconciliation", "month"] as const;

function formatMoney(amount: number): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(amount);
}

function parseDraftNumber(value: string): number {
  const trimmed = value.trim();
  if (trimmed === "" || trimmed === "-") {
    return 0;
  }
  const parsed = Number.parseFloat(trimmed);
  return Number.isFinite(parsed) ? parsed : 0;
}

function targetDraftFromPayload(payload: EomMonthPayload): EomTargetValues {
  return { ...payload.target };
}

function qboDraftFromPayload(payload: EomMonthPayload): EomQboValues {
  return { ...payload.qbo };
}

function TargetFieldGrid({
  values,
  onChange,
  disabled,
}: {
  values: EomTargetValues;
  onChange: (next: EomTargetValues) => void;
  disabled: boolean;
}) {
  const keys = Object.keys(EOM_TARGET_LABELS) as Array<keyof EomTargetValues>;
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
      {keys.map((key) => (
        <div key={key}>
          <Label htmlFor={`eom-target-${key}`}>{EOM_TARGET_LABELS[key]}</Label>
          <Input
            id={`eom-target-${key}`}
            type="number"
            step="0.01"
            className="mt-1.5 font-mono tabular-nums"
            value={String(values[key])}
            disabled={disabled}
            onChange={(event) => {
              onChange({ ...values, [key]: parseDraftNumber(event.target.value) });
            }}
          />
        </div>
      ))}
    </div>
  );
}

function QboFieldGrid({
  values,
  onChange,
  disabled,
}: {
  values: EomQboValues;
  onChange: (next: EomQboValues) => void;
  disabled: boolean;
}) {
  const keys = Object.keys(EOM_QBO_LABELS) as Array<keyof EomQboValues>;
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
      {keys.map((key) => (
        <div key={key}>
          <Label htmlFor={`eom-qbo-${key}`}>{EOM_QBO_LABELS[key]}</Label>
          <Input
            id={`eom-qbo-${key}`}
            type="number"
            step="0.01"
            className="mt-1.5 font-mono tabular-nums"
            value={String(values[key])}
            disabled={disabled}
            onChange={(event) => {
              onChange({ ...values, [key]: parseDraftNumber(event.target.value) });
            }}
          />
        </div>
      ))}
    </div>
  );
}

export function EomReconciliationMonthModal({
  month,
  canWrite,
  onClose,
}: {
  month: string;
  canWrite: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [targetDraft, setTargetDraft] = useState<EomTargetValues | null>(null);
  const [qboDraft, setQboDraft] = useState<EomQboValues | null>(null);
  const [dirty, setDirty] = useState(false);
  const [jeNameDraft, setJeNameDraft] = useState("");
  const [jeUrlDraft, setJeUrlDraft] = useState("");

  const monthQuery = useQuery({
    queryKey: [...EOM_MONTH_QUERY_KEY, month],
    queryFn: () => fetchEomMonth(month),
  });

  const locked = monthQuery.data?.locked ?? false;
  const valuesReadOnly = !canWrite || locked;

  useEffect(() => {
    setDirty(false);
    if (!monthQuery.data) {
      setTargetDraft(null);
      setQboDraft(null);
      setJeNameDraft("");
      setJeUrlDraft("");
      return;
    }
    setTargetDraft(targetDraftFromPayload(monthQuery.data));
    setQboDraft(qboDraftFromPayload(monthQuery.data));
    setJeNameDraft(monthQuery.data.qbo_je_name ?? "");
    setJeUrlDraft(monthQuery.data.qbo_je_url ?? "");
  }, [month, monthQuery.data]);

  const journalEntry = useMemo(() => {
    if (!targetDraft || !qboDraft) {
      return null;
    }
    if (!dirty && monthQuery.data) {
      return monthQuery.data.journal_entry;
    }
    return computeEomJournalEntry(targetDraft, qboDraft);
  }, [dirty, monthQuery.data, qboDraft, targetDraft]);

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!targetDraft || !qboDraft) {
        throw new Error("Values are not loaded.");
      }
      return saveEomMonth(month, { target: targetDraft, qbo: qboDraft });
    },
    onSuccess: (payload) => {
      setDirty(false);
      setTargetDraft(targetDraftFromPayload(payload));
      setQboDraft(qboDraftFromPayload(payload));
      queryClient.setQueryData([...EOM_MONTH_QUERY_KEY, month], payload);
      void queryClient.invalidateQueries({ queryKey: ["accounting", "eom-reconciliation", "months"] });
      onClose();
    },
  });

  const submitMutation = useMutation({
    mutationFn: () =>
      submitEomMonth(month, {
        qbo_je_name: jeNameDraft.trim(),
        qbo_je_url: jeUrlDraft.trim(),
      }),
    onSuccess: (payload) => {
      queryClient.setQueryData([...EOM_MONTH_QUERY_KEY, month], payload);
      void queryClient.invalidateQueries({ queryKey: ["accounting", "eom-reconciliation", "months"] });
      onClose();
    },
  });

  const headerExtra = useMemo(() => {
    if (monthQuery.data?.locked) {
      return (
        <span className="inline-flex items-center gap-1 text-xs text-slate-500">
          <Lock className="h-3.5 w-3.5" aria-hidden />
          Locked
        </span>
      );
    }
    if (monthQuery.data?.saved) {
      return "Saved — still editable until submitted";
    }
    return "Not saved yet";
  }, [monthQuery.data?.locked, monthQuery.data?.saved]);

  const canSubmitJe =
    canWrite &&
    monthQuery.data?.saved &&
    !monthQuery.data.qbo_je_name &&
    jeNameDraft.trim() !== "" &&
    jeUrlDraft.trim() !== "";

  return (
    <DocumentModal
      title={formatMonthLabel(month)}
      headerExtra={headerExtra}
      onClose={onClose}
      showCloseButton={false}
      shellClassName="max-w-5xl"
    >
      {monthQuery.isError ? (
        <p className="text-sm text-red-600">
          {monthQuery.error instanceof Error ? monthQuery.error.message : "Could not load this month."}
        </p>
      ) : monthQuery.isLoading || !targetDraft || !qboDraft ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading month…
        </div>
      ) : (
        (() => {
          const monthData = monthQuery.data;
          if (monthData == null) {
            return (
              <p className="text-sm text-slate-500">Month data is not available.</p>
            );
          }
          return (
        <div className="space-y-8">
          {monthData.qbo_je_name && monthData.qbo_je_url ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
              <span className="text-slate-600">Submitted JE: </span>
              <a
                href={monthData.qbo_je_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-medium text-blue-700 hover:text-blue-800"
              >
                {monthData.qbo_je_name}
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
              </a>
            </div>
          ) : null}

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-900">Target values (Megaventory)</h3>
            <TargetFieldGrid
              values={targetDraft}
              disabled={valuesReadOnly}
              onChange={(next) => {
                setTargetDraft(next);
                setDirty(true);
              }}
            />
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-900">QBO values</h3>
            <QboFieldGrid
              values={qboDraft}
              disabled={valuesReadOnly}
              onChange={(next) => {
                setQboDraft(next);
                setDirty(true);
              }}
            />
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-900">Journal entry</h3>
            {!journalEntry ? (
              <p className="text-sm text-slate-500">Enter values above to compute the journal entry.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="px-3 py-2">QBO account</th>
                      <th className="px-3 py-2 text-right">Debit</th>
                      <th className="px-3 py-2 text-right">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {journalEntry.lines.map((line: EomJournalEntryLine) => (
                      <tr key={line.qbo_account} className="border-b border-slate-100">
                        <td className="px-3 py-2 font-medium text-slate-900">{line.qbo_account}</td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-800">
                          {line.debit > 0 ? formatMoney(line.debit) : "—"}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-800">
                          {line.credit > 0 ? formatMoney(line.credit) : "—"}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-slate-50 font-semibold">
                      <td className="px-3 py-2 text-slate-900">Total</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums">
                        {formatMoney(journalEntry.total_debit)}
                      </td>
                      <td
                        className={cn(
                          "px-3 py-2 text-right font-mono tabular-nums",
                          journalEntry.total_debit !== journalEntry.total_credit && "text-red-700",
                        )}
                      >
                        {formatMoney(journalEntry.total_credit)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {canWrite && monthData.saved && !monthData.qbo_je_name ? (
            <section className="space-y-3 rounded-lg border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-900">Submit journal entry</h3>
              <p className="text-sm text-slate-600">
                After posting in QuickBooks Online, record the journal entry name and link. Submitting locks this month.
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <Label htmlFor="eom-je-name">Journal entry name</Label>
                  <Input
                    id="eom-je-name"
                    className="mt-1.5"
                    value={jeNameDraft}
                    onChange={(event) => setJeNameDraft(event.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="eom-je-url">QuickBooks Online link</Label>
                  <Input
                    id="eom-je-url"
                    type="url"
                    className="mt-1.5"
                    value={jeUrlDraft}
                    onChange={(event) => setJeUrlDraft(event.target.value)}
                  />
                </div>
              </div>
              {submitMutation.isError ? (
                <p className="text-sm text-red-600">
                  {submitMutation.error instanceof Error
                    ? submitMutation.error.message
                    : "Could not submit journal entry."}
                </p>
              ) : null}
            </section>
          ) : null}

          {saveMutation.isError ? (
            <p className="text-sm text-red-600">
              {saveMutation.error instanceof Error ? saveMutation.error.message : "Could not save month."}
            </p>
          ) : null}

          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 pt-4">
            {valuesReadOnly && !canWrite ? (
              <p className="mr-auto text-xs text-slate-500">
                Read-only — ask an administrator for write access to save months.
              </p>
            ) : valuesReadOnly && locked ? (
              <p className="mr-auto text-xs text-slate-500">This month is locked — values are view-only.</p>
            ) : null}
            <Button type="button" variant="secondary" onClick={onClose}>
              {valuesReadOnly ? "Close" : "Cancel"}
            </Button>
            {canSubmitJe ? (
              <Button type="button" disabled={submitMutation.isPending} onClick={() => submitMutation.mutate()}>
                {submitMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ExternalLink className="h-4 w-4" />
                )}
                Submit journal entry
              </Button>
            ) : null}
            {canWrite && !locked ? (
              <HubSaveButton
                type="button"
                dirty={dirty}
                isLoading={saveMutation.isPending}
                onClick={() => saveMutation.mutate()}
              >
                {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save month
              </HubSaveButton>
            ) : null}
          </div>
        </div>
          );
        })()
      )}
    </DocumentModal>
  );
}
